import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    OnDestroy,
    OnInit,
    computed,
    inject,
    Signal,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { DrawerModule } from "primeng/drawer";
import { ToastModule } from "primeng/toast";
import {
    catchError,
    EMPTY,
    filter,
    map,
    of,
    Subscription,
    switchMap,
} from "rxjs";
import {
    GlobalSidebarService,
    ResolvedSidebarProps,
    SidebarProps,
} from "./core/services/ui/global-sidebar.service";
import { GoogleMapsLoaderService } from "./core/services/external/google-maps-loader.service";
import { PragmaAgentLoaderService } from "./core/services/domain/pragma-agent-loader.service";
import { NotificationsService } from "./core/services/notifications/notifications.service";
import { MqttService } from "./core/services/mqtt/mqtt.service";
import { MqttSignalService } from "./core/services/mqtt/mqtt-signal.service";
import { ThemeService } from "./core/services/ui/theme.service";
import {
    UserPermission,
    UserAuthService,
} from "./core/services/auth/user-auth.service";
import { APP_VERSION } from "src/environments/version";
import { environment } from "src/environments/environment";

const PRAGMA_AGENT_TOKEN = environment.pragmaAgentToken ?? "";
const PRAGMA_AGENT_API_URL = environment.pragmaAgentApiUrl ?? "";
import { registerTransform } from "echarts";
import * as ecStat from "echarts-stat";
import "echarts-gl";

const DEFAULT_SIDEBAR_PROPS: ResolvedSidebarProps = {
    autoZIndex: true,
    baseZIndex: 0,
    blockScroll: true,
    dismissible: true,
    fullScreen: false,
    modal: true,
    position: "right",
    style: {},
    styleClass: "w-1/3 max-w-screen",
};

function mergeSidebarProps(props: SidebarProps): ResolvedSidebarProps {
    return {
        autoZIndex: props.autoZIndex ?? DEFAULT_SIDEBAR_PROPS.autoZIndex,
        baseZIndex: props.baseZIndex ?? DEFAULT_SIDEBAR_PROPS.baseZIndex,
        blockScroll: props.blockScroll ?? DEFAULT_SIDEBAR_PROPS.blockScroll,
        dismissible: props.dismissible ?? DEFAULT_SIDEBAR_PROPS.dismissible,
        fullScreen: props.fullScreen ?? DEFAULT_SIDEBAR_PROPS.fullScreen,
        modal: props.modal ?? DEFAULT_SIDEBAR_PROPS.modal,
        position: props.position ?? DEFAULT_SIDEBAR_PROPS.position,
        style: { ...DEFAULT_SIDEBAR_PROPS.style, ...props.style },
        styleClass: props.styleClass ?? DEFAULT_SIDEBAR_PROPS.styleClass,
    };
}

@Component({
    standalone: true,
    selector: "ngx-app",
    templateUrl: "./app.component.html",
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule, RouterModule, ToastModule, DrawerModule],
    providers: [GoogleMapsLoaderService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
    private _mqttSignal: MqttSignalService = inject(MqttSignalService);
    private _sidebarService: GlobalSidebarService =
        inject(GlobalSidebarService);
    private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
    private googleMapsLoader: GoogleMapsLoaderService = inject(
        GoogleMapsLoaderService,
    );
    private pragmaAgentLoader: PragmaAgentLoaderService = inject(
        PragmaAgentLoaderService,
    );
    private _notificationService: NotificationsService =
        inject(NotificationsService);
    private _themeService: ThemeService = inject(ThemeService);
    private _mqttService: MqttService = inject(MqttService);
    private _userService: UserAuthService = inject(UserAuthService);
    private themeSubscription: Subscription = new Subscription();
    isDarkMode: boolean = false;
    readonly pragmaAgentEnabled = environment.pragmaAgentEnabled ?? false;
    readonly agentToken = PRAGMA_AGENT_TOKEN;
    readonly pragmaAgentApiUrl = PRAGMA_AGENT_API_URL;
    readonly embedReady = this.pragmaAgentLoader.embedReady;

    readonly sidebarOptions: Signal<ResolvedSidebarProps> = toSignal(
        this._sidebarService.sidebarOptions$.pipe(
            map((props) => mergeSidebarProps(props)),
        ),
        { initialValue: DEFAULT_SIDEBAR_PROPS },
    );
    readonly sidebarVisible = toSignal(this._sidebarService.sidebarVisible$, {
        initialValue: false,
    });
    readonly contentTemplate = toSignal(this._sidebarService.contentTemplate$, {
        initialValue: undefined,
    });
    readonly headerTemplate = toSignal(this._sidebarService.headerTemplate$, {
        initialValue: undefined,
    });
    readonly footerTemplate = toSignal(this._sidebarService.footerTemplate$, {
        initialValue: undefined,
    });

    readonly hasSuperAdminPermission = computed(() =>
        this._userService.hasPermission(UserPermission.SuperAdmin),
    );

    readonly user = this._userService.user;

    ngOnInit(): void {
        registerTransform((ecStat as any).transform.regression);

        if (!environment.production) {
            console.info(
                "App version:",
                APP_VERSION.version,
                APP_VERSION.commit,
                new Date(APP_VERSION.date),
            );
        }

        of(this.user)
            .pipe(
                filter((user) => !!user),
                switchMap((user) =>
                    this._mqttService.subscribeToTopic(
                        `user/${user()?.token}/notification`,
                    ),
                ),
                catchError((err) => {
                    console.warn("MQTT notification stream error:", err);
                    return EMPTY;
                }),
            )
            .subscribe((data) => {
                console.log("broadcasted data", data);
                const incoming = JSON.parse(data.message);
                const incomingData = JSON.parse(incoming.data);
                const payload =
                    typeof incomingData.data === "string"
                        ? JSON.parse(incomingData.data)
                        : incomingData;

                this._notificationService.displayInfo(
                    payload.component,
                    payload.message,
                );
                this._mqttSignal.setComponent(payload.component);
            });

        this.themeSubscription = this._themeService.darkModeState$.subscribe(
            (isDark) => {
                this.isDarkMode = isDark;
                this._cdr.detectChanges();
            },
        );

        this.googleMapsLoader
            .loadGoogleMaps()
            .then(() => {
                console.info("Initialized maps");
            })
            .catch((error) =>
                console.error("Error loading Google Maps:", error),
            );

        if (this.pragmaAgentEnabled) {
            this.pragmaAgentLoader
                .loadEmbed()
                .then(() => {
                    this._cdr.detectChanges();
                })
                .catch((error: unknown) => {
                    const message =
                        error instanceof Error
                            ? error.message
                            : this.pragmaAgentLoader.getEmbedUnavailableMessage();
                    console.warn(`[Pragma Agent] ${message}`);
                });
        }
    }

    ngOnDestroy(): void {
        if (this.themeSubscription) {
            this.themeSubscription.unsubscribe();
            this._cdr.detectChanges();
        }
        this.googleMapsLoader.removeScript();
    }

    toggleSidebar(value: boolean) {
        if (!value) {
            this._sidebarService.close();
        }
    }
}
